// packages/web/src/App.tsx
import React, { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { HeroBanner } from './components/HeroBanner'
import { ProductCard } from './components/ProductCard'
import { StorefrontLayout } from './layouts/StorefrontLayout'
import { ProductImage } from './components/ProductImage'
import { apiGet } from './lib/api'
import type { Product } from '@buddys-choice/shared'
import { Package, AlertCircle } from 'lucide-react'
import { useSiteConfig } from './context/SiteConfigContext'
import { useStoreCategories, useCategoriesWithProducts, getCategoryLabel } from './lib/storeCategories'

export const StorefrontPage: React.FC = () => {
  const navigate = useNavigate()
  const { config } = useSiteConfig()
  const { categories } = useStoreCategories()
  const { categories: categoriesWithProducts, loading: categoriesLoading } = useCategoriesWithProducts()
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [apiError, setApiError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>()

  useEffect(() => {
    const isLegacyAdmin =
      window.location.hash === '#admin' ||
      new URLSearchParams(window.location.search).get('admin') === 'true'

    if (isLegacyAdmin) {
      navigate({ to: '/admin' })
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault()
        navigate({ to: '/admin' })
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate])

  useEffect(() => {
    fetchCatalog()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedCategory])

  useEffect(() => {
    if (categoriesLoading) return
    if (selectedCategory && !categoriesWithProducts.some((c) => c.slug.toUpperCase() === selectedCategory.toUpperCase())) {
      setSelectedCategory(undefined)
    }
  }, [selectedCategory, categoriesWithProducts, categoriesLoading])

  const fetchCatalog = () => {
    setLoading(true)
    setApiError('')
    apiGet<Product[]>('products', {
      search: searchQuery || undefined,
      category: selectedCategory || undefined,
      pageSize: 30,
      isActive: true,
    })
      .then((data) => {
        setProducts(Array.isArray(data) ? data : [])
      })
      .catch((err: unknown) => {
        setProducts([])
        setApiError((err as Error).message || 'Could not load catalogue. Is the API server running?')
      })
      .finally(() => setLoading(false))
  }

  return (
    <StorefrontLayout
      selectedCategory={selectedCategory}
      setSelectedCategory={setSelectedCategory}
      onSearch={setSearchQuery}
    >
      <HeroBanner
        onSelectCategory={(cat) => setSelectedCategory(cat)}
        selectedCategory={selectedCategory}
        showcaseImage={
          products.find((p) => p.isFeatured && p.heroImageUrl)?.heroImageUrl
          ?? products.find((p) => p.heroImageUrl)?.heroImageUrl
          ?? null
        }
      />

      {!categoriesLoading && categoriesWithProducts.length > 0 && (
      <section className="max-w-[1800px] mx-auto px-4 sm:px-8 lg:px-12 pt-14 animate-fade-in-up">
        <div className="mb-6">
          <h3 className="text-xl font-title font-bold text-[var(--text-primary)]">
            {config.category_section_title || 'SHOP BY CATEGORY'}
          </h3>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">
            {config.category_section_subtitle}
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {categoriesWithProducts.map((item) => {
            const isSelected = selectedCategory?.toUpperCase() === item.slug.toUpperCase()
            const catImage = item.imageUrl || products.find((p) => p.category.toUpperCase() === item.slug.toUpperCase())?.heroImageUrl
            return (
              <button
                key={item.slug}
                type="button"
                onClick={() => setSelectedCategory(item.slug)}
                className={`group cursor-pointer rounded-2xl overflow-hidden relative aspect-[4/3] border transition-all duration-300 text-left w-full ${
                  isSelected
                    ? 'border-[var(--gold-main)] ring-2 ring-[var(--gold-main)]/50'
                    : 'border-[var(--border)] hover:border-[var(--gold-main)]/40'
                }`}
              >
                <ProductImage
                  src={catImage ?? null}
                  alt={item.label}
                  className="w-full h-full"
                  imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)]/90 via-[var(--bg-main)]/30 to-transparent p-3 flex flex-col justify-end">
                  <span className="text-xs font-title font-bold text-[var(--text-primary)]">{item.label}</span>
                </div>
              </button>
            )
          })}
        </div>
      </section>
      )}

      <section id="catalog" className="max-w-[1800px] mx-auto px-4 sm:px-8 lg:px-12 py-14 animate-fade-in-up">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-4 border-b border-[var(--gold-main)]/20">
          <div>
            <h2 className="text-2xl sm:text-3xl font-title font-bold text-[var(--text-primary)]">
              {selectedCategory
                ? `${getCategoryLabel(selectedCategory, categories).toUpperCase()} COLLECTION`
                : config.catalogue_section_title || 'CURATED JEWELLERY CATALOGUE'}
            </h2>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              {searchQuery ? `Results for "${searchQuery}"` : config.catalogue_section_subtitle}
            </p>
          </div>
          <span className="text-xs text-[var(--gold-main)] font-mono font-semibold bg-[var(--gold-main)]/10 px-3 py-1 rounded-full border border-[var(--gold-main)]/20">
            {products.length} {products.length === 1 ? 'item' : 'items'}
          </span>
        </div>

        {apiError && (
          <div className="alert alert-error flex items-center gap-2 text-sm mb-6">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {apiError}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-72 sm:h-80 bg-[var(--bg-surface-2)] rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state py-16 text-center space-y-3">
            <Package className="w-12 h-12 text-[var(--gold-main)]/40 mx-auto" />
            <h3 className="font-title text-lg">No products in catalogue</h3>
            <p className="text-xs text-[var(--text-muted)]">
              {apiError ? 'Start the API and seed the database.' : 'Try a different search or category.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-6">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>
    </StorefrontLayout>
  )
}

export default StorefrontPage
